import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import {
  hasSecondaryBenefits,
  resolveOrderBenefits,
  resolveOrderSecondaryBenefits,
} from "helpers/orderBenefits";
import {
  centsToDollars,
  isNoMaximumOOPDollars,
  parsePrice,
} from "helpers/price";
import { EngineProvenanceBanner } from "pages/CreateOrder/components/PatientBenefitsSection/components/Benefits/components/EngineProvenanceBanner";
import { PatientBenefits as PatientBenefitsDetails } from "pages/CreateOrder/components/PatientBenefitsSection/components/Benefits/components/PatientBenefits";
import { PlanCoverageCard } from "pages/CreateOrder/components/PatientBenefitsSection/components/PlanCoverageCard";
import { readNoDeductibleChoice } from "pages/CreateOrder/components/PatientBenefitsSection/noDeductibleChoice";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { IOrder } from "types/Order/Order";

import { DetailField, SectionHeader } from "../SectionParts";

const StyledDialogContent = styled.div`
  /* Ensure content fills the dialog */
  width: 100%;
  overflow-x: hidden;

  /* Hide the unnecessary vertical scrollbar on the inner tab nav */
  .p-tabview-nav {
    overflow-y: hidden !important;
  }
`;

interface IPatientBenefits {
  deductible: number;
  outOfPocketMax: number;
  flatCopay: number;
  coinsurancePercent: number;
  deductible2: number;
  outOfPocketMax2: number;
  flatCopay2: number;
  coinsurancePercent2: number;
  payerName: string;
  payerName2: string;
  placeOfService: string;
  locality: string;
  memberId: string;
  memberId2: string;
  serviceType: string;
  serviceType2: string;
}

function BenefitRow({
  label,
  value,
  testId,
  missing,
}: {
  label: string;
  value: string;
  testId: string;
  missing?: { text: string; testId: string };
}) {
  return (
    <div className="flex align-items-center justify-content-between">
      <span
        className="flex align-items-center gap-2 text-base"
        style={{ color: "var(--brand-gray-600)" }}
      >
        {label}
        {missing && (
          <i
            className="pi pi-info-circle benefit-missing-hint"
            data-testid={missing.testId}
            data-pr-tooltip={missing.text}
            style={{ color: "var(--red-600)" }}
          />
        )}
      </span>
      <span
        className="text-base font-bold"
        style={{ color: "var(--brand-black)" }}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

const formatDollars = (value?: number | null) =>
  value == null ? "—" : parsePrice(value);

const formatOutOfPocket = (value?: number | null) => {
  if (value == null) return "—";
  return isNoMaximumOOPDollars(value) ? "No maximum" : parsePrice(value);
};

const formatPercent = (value?: number | null) =>
  value == null ? "—" : `${value}%`;

// Mirrors the create page's "info missing" rules (Benefits.tsx); a saved estimate stores a missing benefit as $0.
const missingBenefitFlags = (bd: any) => {
  const r = bd?.response;
  const noDeductible =
    readNoDeductibleChoice(bd) || r?.deductibleNotApplicable === true;
  return {
    deductible:
      !!bd &&
      (r?.remainingDeductible === null ||
        r?.isRemainingDeductibleEmpty === true) &&
      !noDeductible,
    outOfPocketMax:
      !!bd &&
      r?.isRemainingOutOfPocketMaxInfinity !== true &&
      (r?.remainingOutOfPocketMax === null ||
        r?.isRemainingOutOfPocketMaxEmpty === true),
    copay: !!bd && r?.flatCopay === null,
    coinsurance: !!bd && r?.coinsurancePercent === null,
  };
};

const benefitBoxStyle = {
  background:
    "color-mix(in srgb, var(--brand-purple-600) 6%, var(--brand-white))",
  borderColor: "color-mix(in srgb, var(--brand-purple-600) 20%, transparent)",
};

function PatientBenefits({ order }: { order: IOrder }) {
  const { setValue, watch } = useFormContext<IPatientBenefits>();
  const payerName2Field = (order.serviceDetails as any)?.secondaryPayerName;
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const hasBenefitDetails = !!order?.benefitDetails;
  const hasSecondaryBenefitDetails = !!order?.secondaryBenefitDetails;
  const hasSecondaryInsurance = !!order?.secondaryPayer;
  const planSummary = order.benefitDetails?.response?.planCoverageSummary;
  const secondaryPlanSummary =
    order.secondaryBenefitDetails?.response?.planCoverageSummary;
  const planName = planSummary?.PlanNetworkName || planSummary?.PlanName;
  const planName2 =
    secondaryPlanSummary?.PlanNetworkName || secondaryPlanSummary?.PlanName;
  const payerName = watch("payerName");
  const payerName2 = watch("payerName2");
  const memberId = watch("memberId");
  const memberId2 = watch("memberId2");
  const serviceType = watch("serviceType");
  const serviceType2 = watch("serviceType2");
  const deductible = watch("deductible");
  const deductible2 = watch("deductible2");
  const outOfPocketMax = watch("outOfPocketMax");
  const outOfPocketMax2 = watch("outOfPocketMax2");
  const flatCopay = watch("flatCopay");
  const flatCopay2 = watch("flatCopay2");
  const coinsurancePercent = watch("coinsurancePercent");
  const coinsurancePercent2 = watch("coinsurancePercent2");
  const flags = missingBenefitFlags(order.benefitDetails);
  const flags2 = missingBenefitFlags(order.secondaryBenefitDetails);

  useEffect(() => {
    setValue("payerName", (order.serviceDetails as any)?.payerName || "Any");
    setValue(
      "payerName2",
      (order.serviceDetails as any)?.secondaryPayerName || "Any"
    );
    // CLE-2635 — Athena orders can have no quote but real eligibility values.
    const benefits = resolveOrderBenefits(order);
    setValue("deductible", centsToDollars(benefits.remainingDeductibleInCents));
    setValue("outOfPocketMax", centsToDollars(benefits.outOfPocketMaxInCents));
    setValue("flatCopay", centsToDollars(benefits.flatCopayInCents));
    setValue("coinsurancePercent", benefits.coinsurancePercent);
    setValue(
      "memberId",
      order?.benefitDetails?.request?.member?.memberID ||
        order?.patient?.patientBenefits?.memberID ||
        null
    );
    setValue(
      "serviceType",
      mapPayerNameToCodeForDropdown.find(
        (item: { value: string; name: string }) =>
          item.value ===
          (order?.benefitDetails?.request?.practiceTypeCode ||
            order?.serviceDetails?.practiceTypeCode)
      )?.name ?? "All Other Medical"
    );

    // Set secondary insurance values
    if (hasSecondaryBenefits(order)) {
      const secondary = resolveOrderSecondaryBenefits(order);
      setValue(
        "memberId2",
        order?.secondaryBenefitDetails?.request?.member?.memberID ||
          order?.patient?.secondaryPatientBenefits?.memberID ||
          null
      );
      setValue(
        "serviceType2",
        mapPayerNameToCodeForDropdown.find(
          (item: { value: string; name: string }) =>
            item.value ===
            (order?.secondaryBenefitDetails?.request?.practiceTypeCode ||
              order?.serviceDetails?.secondaryPracticeTypeCode)
        )?.name ?? "All Other Medical"
      );
      setValue(
        "deductible2",
        centsToDollars(secondary.remainingDeductibleInCents)
      );
      setValue(
        "outOfPocketMax2",
        centsToDollars(secondary.outOfPocketMaxInCents)
      );
      setValue("flatCopay2", centsToDollars(secondary.flatCopayInCents));
      setValue("coinsurancePercent2", secondary.coinsurancePercent);
    }
  }, [order, setValue]);

  return (
    <section
      className="px-3 pb-3 border-bottom-1"
      data-tour={TourAnchor.PatientBenefits}
      style={{
        borderColor: "var(--brand-gray-200)",
        marginLeft: "-1rem",
        marginRight: "-1rem",
      }}
    >
      <Tooltip
        target=".benefit-missing-hint"
        style={{ fontSize: "14px", width: "250px" }}
      />
      <SectionHeader icon="pi pi-shield" title="Insurance & Benefits">
        {hasBenefitDetails && (
          <Button
            className="p-button-text p-button-plain p-button-sm"
            onClick={() => setShowDetailsDialog(true)}
          >
            See Details
          </Button>
        )}
      </SectionHeader>
      <Dialog
        header="Eligibility Benefits Details"
        visible={showDetailsDialog}
        onHide={() => setShowDetailsDialog(false)}
        style={{ width: "900px", maxWidth: "90vw" }}
        dismissableMask
        blockScroll
      >
        <StyledDialogContent>
          {hasSecondaryBenefitDetails ? (
            <TabView>
              <TabPanel header="Primary Benefits">
                <PlanCoverageCard
                  benefitDetails={order.benefitDetails}
                  className="mt-2"
                />
                <PatientBenefitsDetails
                  benefitDetailsField={order.benefitDetails}
                  deductibleField={deductible}
                  outOfPocketMaxField={outOfPocketMax}
                  coinsuranceField={coinsurancePercent}
                  copayField={flatCopay}
                  readOnly
                  fullWidth
                />
              </TabPanel>
              <TabPanel header="Secondary Benefits">
                <PlanCoverageCard
                  benefitDetails={order.secondaryBenefitDetails}
                  className="mt-2"
                />
                <PatientBenefitsDetails
                  benefitDetailsField={order.secondaryBenefitDetails}
                  deductibleField={deductible2}
                  outOfPocketMaxField={outOfPocketMax2}
                  coinsuranceField={coinsurancePercent2}
                  copayField={flatCopay2}
                  readOnly
                  fullWidth
                />
              </TabPanel>
            </TabView>
          ) : (
            <>
              <PlanCoverageCard
                benefitDetails={order.benefitDetails}
                className="mt-2"
              />
              <PatientBenefitsDetails
                benefitDetailsField={order.benefitDetails}
                deductibleField={deductible}
                outOfPocketMaxField={outOfPocketMax}
                coinsuranceField={coinsurancePercent}
                copayField={flatCopay}
                readOnly
                fullWidth
              />
            </>
          )}
        </StyledDialogContent>
      </Dialog>
      <div className="grid mt-3">
        {hasSecondaryInsurance && (
          <div className="col-12 mb-3">
            <span className="text-base font-bold text-700">
              Primary Insurance
            </span>
          </div>
        )}
        <DetailField label="Payer" value={payerName} testId="payerName" />
        <DetailField label="Plan" value={planName} />
        <DetailField label="Member ID" value={memberId} testId="memberId" />
        <DetailField
          label="Service type"
          value={serviceType}
          testId="serviceType"
        />
        <div className="col-12">
          <div
            className="p-3 border-1 border-round-2xl flex flex-column gap-3"
            style={benefitBoxStyle}
          >
            <BenefitRow
              label="Deductible remaining"
              value={formatDollars(deductible)}
              testId="deductible"
              missing={
                flags.deductible
                  ? {
                      text: "Deductible info is missing.",
                      testId: "deductibleMissingHint",
                    }
                  : undefined
              }
            />
            <BenefitRow
              label="Out-of-pocket remaining"
              value={formatOutOfPocket(outOfPocketMax)}
              testId="outOfPocketMax"
              missing={
                flags.outOfPocketMax
                  ? {
                      text: "Out-of-pocket max info is missing.",
                      testId: "oopMissingHint",
                    }
                  : undefined
              }
            />
            <BenefitRow
              label="Copay"
              value={formatDollars(flatCopay)}
              testId="flatCopay"
              missing={
                flags.copay
                  ? {
                      text: "Copay info is missing.",
                      testId: "copayMissingHint",
                    }
                  : undefined
              }
            />
            {/* CLE-2816 / CLE-2951 — the same yellow notes as the create / redo / copy panel. */}
            <EngineProvenanceBanner
              response={order?.benefitDetails?.response}
              benefit="FLAT_COPAY"
              testId="orderView-engineProvenance-flatCopay"
            />
            <BenefitRow
              label="Coinsurance"
              value={formatPercent(coinsurancePercent)}
              testId="coinsurancePercent"
              missing={
                flags.coinsurance
                  ? {
                      text: "Coinsurance info is missing.",
                      testId: "coinsuranceMissingHint",
                    }
                  : undefined
              }
            />
            <EngineProvenanceBanner
              response={order?.benefitDetails?.response}
              benefit="COINSURANCE_PERCENT"
              testId="orderView-engineProvenance-coinsurancePercent"
            />
          </div>
        </div>

        {hasSecondaryInsurance && (
          <>
            <div className="col-12 mt-3 mb-3">
              <span className="text-base font-bold text-700">
                Secondary Insurance
              </span>
            </div>
            {payerName2Field && (
              <DetailField
                label="Secondary Payer"
                value={payerName2}
                testId="payerName2"
              />
            )}
            <DetailField label="Plan" value={planName2} />
            <DetailField
              label="Member ID"
              value={memberId2}
              testId="memberId2"
            />
            <DetailField
              label="Service type"
              value={serviceType2}
              testId="serviceType2"
            />
            <div className="col-12">
              <div
                className="p-3 border-1 border-round-2xl flex flex-column gap-3"
                style={benefitBoxStyle}
              >
                <BenefitRow
                  label="Deductible remaining"
                  value={formatDollars(deductible2)}
                  testId="deductible2"
                  missing={
                    flags2.deductible
                      ? {
                          text: "Deductible info is missing.",
                          testId: "deductibleMissingHint2",
                        }
                      : undefined
                  }
                />
                <BenefitRow
                  label="Out-of-pocket remaining"
                  value={formatOutOfPocket(outOfPocketMax2)}
                  testId="outOfPocketMax2"
                  missing={
                    flags2.outOfPocketMax
                      ? {
                          text: "Out-of-pocket max info is missing.",
                          testId: "oopMissingHint2",
                        }
                      : undefined
                  }
                />
                <BenefitRow
                  label="Copay"
                  value={formatDollars(flatCopay2)}
                  testId="flatCopay2"
                  missing={
                    flags2.copay
                      ? {
                          text: "Copay info is missing.",
                          testId: "copayMissingHint2",
                        }
                      : undefined
                  }
                />
                <EngineProvenanceBanner
                  response={order?.secondaryBenefitDetails?.response}
                  benefit="FLAT_COPAY"
                  testId="orderView-engineProvenance-flatCopay2"
                />
                <BenefitRow
                  label="Coinsurance"
                  value={formatPercent(coinsurancePercent2)}
                  testId="coinsurancePercent2"
                  missing={
                    flags2.coinsurance
                      ? {
                          text: "Coinsurance info is missing.",
                          testId: "coinsuranceMissingHint2",
                        }
                      : undefined
                  }
                />
                <EngineProvenanceBanner
                  response={order?.secondaryBenefitDetails?.response}
                  benefit="COINSURANCE_PERCENT"
                  testId="orderView-engineProvenance-coinsurancePercent2"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default PatientBenefits;
